import React, { useMemo, useState } from "react";
import { Entity } from "@common/types/entity";
import { WorkspaceMember } from "@common/types/workspace";
import { Checkbox } from "@design-system/Checkbox/Checkbox";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { ENTITY_TYPE_LABELS } from "@components/settings/EntitySettings/entityTypeLabels";
import { SearchFilter } from "@design-system/SearchFilter/SearchFilter";
import { ConsolidationMembersCell } from "./ConsolidationMembersCell";
import { ConsolidationTableSelect } from "./ConsolidationTableSelect";
import styles from "./ConsolidationSelectEntitiesStep.module.scss";

export type ConsolidationRole = "parent" | "subsidiary";

const ROLE_LABELS: Record<ConsolidationRole, string> = {
    parent: "Parent",
    subsidiary: "Subsidiary",
};

const ROLE_OPTIONS = (
    ["parent", "subsidiary"] satisfies ConsolidationRole[]
).map((value) => ({ value, label: ROLE_LABELS[value] }));

interface ConsolidationSelectEntitiesStepProps {
    entities: Entity[];
    selectedEntityUuids: string[];
    parentEntityUuid: string | null;
    membersByEntityId: Map<number, WorkspaceMember[]>;
    onToggleEntity: (uuid: string) => void;
    onToggleAll: (filteredUuids: string[]) => void;
    onSetRole: (uuid: string, role: ConsolidationRole) => void;
}

export const ConsolidationSelectEntitiesStep: React.FC<
    ConsolidationSelectEntitiesStepProps
> = ({
    entities,
    selectedEntityUuids,
    parentEntityUuid,
    membersByEntityId,
    onToggleEntity,
    onToggleAll,
    onSetRole,
}) => {
    const [search, setSearch] = useState("");

    const selectedSet = useMemo(
        () => new Set(selectedEntityUuids),
        [selectedEntityUuids],
    );

    const filteredEntities = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) {
            return entities;
        }
        return entities.filter((entity) =>
            (entity.name ?? "").toLowerCase().includes(query),
        );
    }, [entities, search]);

    const filteredUuids = useMemo(
        () => filteredEntities.map((entity) => entity.uuid),
        [filteredEntities],
    );
    const selectedFilteredCount = filteredUuids.filter((uuid) =>
        selectedSet.has(uuid),
    ).length;
    const areAllSelected =
        filteredUuids.length > 0 &&
        selectedFilteredCount === filteredUuids.length;
    const isPartiallySelected = selectedFilteredCount > 0 && !areAllSelected;

    return (
        <div className={styles.step}>
            <div className={styles.searchRow}>
                <SearchFilter
                    filter={search || undefined}
                    onChange={(value) => setSearch(value ?? "")}
                    placeholder="Search…"
                    minLength={1}
                    debounceTime={0}
                />
            </div>
            <div className={styles.table}>
                <div className={styles.headerRow}>
                    <Checkbox
                        checked={areAllSelected}
                        indeterminate={isPartiallySelected}
                        onCheckedChange={() => onToggleAll(filteredUuids)}
                        className={styles.entityCell}
                        label={
                            <span className={styles.headerLabel}>Entity</span>
                        }
                    />
                    <span>Entity type</span>
                    <span>Ownership</span>
                    <span>Members</span>
                    <span className={styles.roleHeader}>Role</span>
                </div>
                {filteredEntities.map((entity) => {
                    const isChecked = selectedSet.has(entity.uuid);
                    const role: ConsolidationRole =
                        parentEntityUuid === entity.uuid
                            ? "parent"
                            : "subsidiary";
                    return (
                        <div key={entity.uuid} className={styles.row}>
                            <Checkbox
                                checked={isChecked}
                                onCheckedChange={() =>
                                    onToggleEntity(entity.uuid)
                                }
                                className={styles.entityCell}
                                label={
                                    <span className={styles.entityLabel}>
                                        <EntityAvatar
                                            size="sm"
                                            entity={entity}
                                            aria-hidden
                                        />
                                        <span className={styles.entityName}>
                                            {entity.name ?? "Untitled entity"}
                                        </span>
                                    </span>
                                }
                            />
                            <span className={styles.entityType}>
                                {entity.legalType
                                    ? ENTITY_TYPE_LABELS[entity.legalType]
                                    : "-"}
                            </span>
                            <span className={styles.ownership}>
                                {isChecked && role === "subsidiary"
                                    ? "100%"
                                    : "-"}
                            </span>
                            <div className={styles.membersCell}>
                                <ConsolidationMembersCell
                                    members={
                                        membersByEntityId.get(entity.id) ?? []
                                    }
                                />
                            </div>
                            <div className={styles.roleCell}>
                                {isChecked ? (
                                    <ConsolidationTableSelect
                                        value={role}
                                        options={ROLE_OPTIONS}
                                        onChange={(value) =>
                                            onSetRole(entity.uuid, value)
                                        }
                                    />
                                ) : null}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
